import { useCallback, useMemo, useState } from 'react'
import { AgxToastState } from 'types/commonTypes'
import {
  CampaignDetailModel,
  StaffUpdateBlankContractDetailsForCampaignRequestBlankSalesContractStatus,
  UploadedDocument,
  useInvalidateUseStaffGetBlankContractDetailsForCampaign,
  useInvalidateUseStaffGetPropertyInformationProgressForCampaign,
  useStaffGetTenancyInformationForCampaign,
  useStaffUpdateBlankContractDetailsForCampaign,
} from '@urbanx/agx.campaigns.client'
import {
  ComplianceData,
  isTenancyAgreementRequired,
} from './contractCompliance'

interface UseContractModalStateArgs {
  campaign?: CampaignDetailModel | undefined
  updateToastState: (toastState: AgxToastState) => void
}

interface ContractModalState {
  isModalOpen: boolean
  setIsModalOpen: (value: boolean) => void
  showTenancyAgreement: boolean
  handleModalComplete: (payload: {
    complianceData: ComplianceData
    uploadedDocument: UploadedDocument
    isFinalSelected: boolean
  }) => Promise<void>
}

const useContractModalState = ({
  campaign,
  updateToastState,
}: UseContractModalStateArgs): ContractModalState => {
  const [isModalOpen, setIsModalOpen] = useState(false)

  const { data: tenancyInformationResponse } =
    useStaffGetTenancyInformationForCampaign(
      campaign?.campaignId ?? '',
      !!campaign?.campaignId
    )

  const invalidateStaffGetBlankContractDetails =
    useInvalidateUseStaffGetBlankContractDetailsForCampaign()
  const invalidateStaffGetPropertyInformationProgress =
    useInvalidateUseStaffGetPropertyInformationProgressForCampaign()

  const { staffUpdateBlankContractDetailsForCampaign } =
    useStaffUpdateBlankContractDetailsForCampaign({
      onSuccess: () => undefined,
      onError: () => undefined,
    })

  const showTenancyAgreement = useMemo(() => {
    return isTenancyAgreementRequired(
      tenancyInformationResponse?.tenantedPropertyDetails?.tenancyType
    )
  }, [tenancyInformationResponse?.tenantedPropertyDetails?.tenancyType])

  const handleModalComplete = useCallback(
    async ({
      uploadedDocument,
      isFinalSelected,
    }: {
      complianceData: ComplianceData
      uploadedDocument: UploadedDocument
      isFinalSelected: boolean
    }) => {
      if (!campaign?.campaignId) return

      try {
        await staffUpdateBlankContractDetailsForCampaign({
          campaignId: campaign.campaignId,
          body: {
            blankSalesContract: uploadedDocument,
            blankSalesContractStatus: isFinalSelected
              ? StaffUpdateBlankContractDetailsForCampaignRequestBlankSalesContractStatus.Final
              : StaffUpdateBlankContractDetailsForCampaignRequestBlankSalesContractStatus.Draft,
          },
        })
        await Promise.all([
          invalidateStaffGetBlankContractDetails(campaign.campaignId),
          invalidateStaffGetPropertyInformationProgress(campaign.campaignId),
        ])
        updateToastState({
          color: 'success',
          message: 'Contract saved and sent to REX',
          open: true,
        })
      } catch {
        updateToastState({
          color: 'error',
          message: 'Error saving contract',
          open: true,
        })
      }
    },
    [
      campaign?.campaignId,
      invalidateStaffGetBlankContractDetails,
      invalidateStaffGetPropertyInformationProgress,
      staffUpdateBlankContractDetailsForCampaign,
      updateToastState,
    ]
  )

  return {
    isModalOpen,
    setIsModalOpen,
    showTenancyAgreement,
    handleModalComplete,
  }
}

export default useContractModalState
